import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
  toApiError,
  useApi,
  type BookingResponse,
  type OtaEarlyDepartureReason,
} from '@/shared/api';
import { minorFromMajor } from '@/shared/lib/money';
import {
  departureDateError,
  earlyDepartureComplete,
  earlyDepartureProblemText,
} from './earlyDeparturePresentation';
import {
  invalidateEarlyDeparture,
  useCancelEarlyDeparturePlan,
} from './useCancelEarlyDeparturePlan';
import { useEarlyDepartureCommandIds } from './useEarlyDepartureCommandIds';

export function useOtaEarlyDeparture(
  booking: BookingResponse | undefined,
  open: boolean,
  /** Schließt den Dialog nach dem Speichern – vorher blieb er offen und wurde zu „Plan ändern“. */
  onDone?: () => void,
) {
  const api = useApi();
  const client = useQueryClient();
  const [departureDate, setDepartureDateState] = useState(booking?.checkInDate ?? '');
  const [reason, setReasonState] = useState<OtaEarlyDepartureReason>('Private');
  const [refundAmount, setRefundAmountState] = useState('');
  const [refundReason, setRefundReasonState] = useState('');
  const [serverDateError, setServerDateError] = useState<string | null>(null);
  const bookingId = booking?.id;
  const commandIds = useEarlyDepartureCommandIds(bookingId);
  const checkInDate = booking?.checkInDate;
  const plannedDepartureDate = booking?.earlyDeparturePlan?.plannedDepartureDate;
  const objectToday = booking?.objectToday ?? null;
  const editingPlan = !!booking?.earlyDeparturePlan;
  const mode: 'actual' | 'plan' =
    editingPlan || (!!objectToday && departureDate > objectToday) ? 'plan' : 'actual';
  const ledger = useQuery({
    queryKey: ['ota-refunds', booking?.id ?? ''],
    queryFn: () => api.otaRefunds.get(booking!.id),
    enabled: open && !!booking && mode === 'actual' && reason === 'Defect',
  });
  const mutation = useMutation<unknown, unknown, void>({
    mutationFn: () => {
      if (!booking) throw new Error('Die Buchung fehlt.');
      if (mode === 'plan') {
        return api.bookings.upsertEarlyDeparturePlan(booking.id, {
          commandId: commandIds.get('plan'),
          plannedDepartureDate: departureDate,
          reason: 'Private',
          expectedVersion: booking.capabilities?.otaEarlyDepartureExpectedVersion ?? '',
          expectedConcurrency: booking.earlyDeparturePlan?.concurrency ?? null,
        });
      }
      if (reason === 'Defect' && !ledger.data) throw new Error('Der Erstattungskontext fehlt.');
      const amountMinor =
        reason === 'Defect' ? minorFromMajor(refundAmount, ledger.data!.currency) : null;
      return api.bookings.recordEarlyDeparture(booking.id, {
        operationId: commandIds.get('actual'),
        actualDepartureDate: departureDate,
        reason,
        refundAmountMinor: amountMinor,
        refundCurrency: reason === 'Defect' ? ledger.data!.currency : null,
        refundReason: reason === 'Defect' ? refundReason.trim() : null,
        expectedVersion:
          reason === 'Defect'
            ? ledger.data!.expectedVersion
            : (booking.capabilities?.otaEarlyDepartureExpectedVersion ?? ''),
      });
    },
    onSuccess: async () => {
      if (mode === 'plan') {
        commandIds.reset('plan', 'cancel');
      } else {
        commandIds.reset('actual');
      }
      await invalidateEarlyDeparture(client);
      toast.success(mode === 'plan' ? 'Vorzeitige Abreise geplant' : 'Vorzeitige Abreise erfasst');
      onDone?.();
    },
    onError: async (error) => {
      const problem = await toApiError(error);
      const message = earlyDepartureProblemText(problem);
      if (problem.status === 400 || problem.fieldErrors) setServerDateError(message);
      toast.error(message);
    },
  });
  const cancelMutation = useCancelEarlyDeparturePlan(booking, commandIds);
  useEffect(() => {
    if (!open || !checkInDate) return;
    setDepartureDateState(plannedDepartureDate ?? checkInDate);
    setReasonState('Private');
    setRefundAmountState('');
    setRefundReasonState('');
    setServerDateError(null);
  }, [bookingId, checkInDate, plannedDepartureDate, open]);

  const localDateError = departureDateError(booking, departureDate, mode);
  const canSubmit =
    !mutation.isPending &&
    !localDateError &&
    !serverDateError &&
    earlyDepartureComplete({
      booking,
      mode,
      reason,
      ledger: ledger.data,
      refundAmount,
      refundReason,
    });

  // Jede geänderte Eingabe ist ein neuer Befehl: alte Befehls-Ids gelten nicht mehr.
  const edit =
    <T>(set: (value: T) => void, current: T, ...kinds: Array<'plan' | 'actual'>) =>
    (value: T) => {
      if (value !== current) commandIds.reset(...kinds);
      set(value);
      setServerDateError(null);
    };

  return {
    departureDate,
    mode,
    reason,
    refundAmount,
    refundReason,
    ledger: ledger.data,
    pending: mutation.isPending || cancelMutation.isPending,
    canSubmit,
    dateError: serverDateError ?? localDateError,
    submit: () => mutation.mutate(),
    cancelPlan: () => cancelMutation.mutate(),
    setDepartureDate: edit(setDepartureDateState, departureDate, 'plan', 'actual'),
    setReason: edit(setReasonState, reason, 'actual'),
    setRefundAmount: edit(setRefundAmountState, refundAmount, 'actual'),
    setRefundReason: edit(setRefundReasonState, refundReason, 'actual'),
  };
}
